// Questionnaire santé (14/10): pure rules, type imports only (runs as is under Node for the targeted checks).
import type { Bilan, HealthAnswer } from "./bilan-state";

export type HealthStatus = "attente" | "valide" | "echange";
export interface HealthConfig { required: boolean; requireTraining: boolean; requireBooking: boolean }
export interface HealthAnswerRow { id: string; q: string; a: HealthAnswer; detail?: string }
/** Immutable copy of what was sent. `answers: null` = no answer kept (talk directly, or no consent); `purged` = the member's own
 *  answers were removed from memory after the health-data consent was withdrawn (never restored by a new consent). */
export interface HealthSnapshot { readonly source: "demo" | "bilan"; readonly submittedAt: string; readonly answers: readonly HealthAnswerRow[] | null; readonly purged?: true }
export interface HealthReview { status: HealthStatus; coach?: string; reviewedAt?: string; comment?: string }
export interface HealthState { config: HealthConfig; snapshot: HealthSnapshot | null; review: HealthReview }

/** Gate: blocked only when explicitly required for this action, a snapshot exists, and the review is not « valide ». The answers
 *  themselves never decide anything (no « Oui » → échange). */
export function healthGate(s: HealthState, action: "training" | "booking"): HealthStatus | null {
  if (!s.config.required || !s.snapshot) return null;
  if (action === "training" && !s.config.requireTraining) return null;
  if (action === "booking" && !s.config.requireBooking) return null;
  return s.review.status === "valide" ? null : s.review.status;
}

/** Frozen snapshot of a locally submitted bilan: its own answers in question order, ONLY with the health-data consent and the
 *  « questions » choice; otherwise (no consent, « parler », no answer) → null, nothing kept. */
export function snapshotOf(b: Pick<Bilan, "healthChoice" | "health">, at: string, questions: readonly { id: string; q: string }[], consentSensibles: boolean): HealthSnapshot {
  const rows = consentSensibles && b.healthChoice === "questions"
    ? questions.flatMap(x => { const h = (b.health as Record<string, { v: HealthAnswer; detail: string } | undefined>)[x.id];
        return h ? [Object.freeze({ id: x.id, q: x.q, a: h.v, ...(h.detail.trim() ? { detail: h.detail.trim() } : {}) })] : []; })
    : [];
  return Object.freeze({ source: "bilan" as const, submittedAt: at, answers: rows.length ? Object.freeze(rows) : null });
}

/** Consent withdrawn: the member's OWN snapshot loses its answers (new frozen object, never mutated). The DEMO fixture is not
 *  member data and is only render-guarded. A later consent never brings purged answers back (nothing is kept to restore). */
export function purgeOwnAnswers(snap: HealthSnapshot | null): HealthSnapshot | null {
  if (!snap || snap.source !== "bilan" || !snap.answers) return snap;
  return Object.freeze({ source: snap.source, submittedAt: snap.submittedAt, answers: null, purged: true as const });
}

/** What the answers page may read: nothing without the health-data consent (neutral state), else the snapshot's answers. */
export function visibleAnswers(snap: HealthSnapshot, consentSensibles: boolean): { kind: "no-consent" } | { kind: "purged" } | { kind: "none" } | { kind: "answers"; rows: readonly HealthAnswerRow[] } {
  if (!consentSensibles) return { kind: "no-consent" };
  if (snap.purged) return { kind: "purged" };
  return snap.answers ? { kind: "answers", rows: snap.answers } : { kind: "none" };
}
